<template>

    <div>
        <b-table :items="value.map((ir, ix) => ({ index: ix, value: ir }))" :fields="[
            { key: 'name' },
            { key: 'requiresNPI', label: 'Requires NPI' },
            { key: 'actions', label: '', class: 'actions' }
        ]" class="mb-0" small bordered>
            <template #cell(name)="{ item, index }">
                <b-input v-model="editingRowData.name" v-if="editingRowIndex === index" autofocus maxlength="50" />
                <template v-else>{{ item.value.name }}</template>
            </template>
            <template #cell(requiresNPI)="{ item, index }">
                <b-check v-model="editingRowData.requiresNPI" v-if="editingRowIndex === index" />
                <template v-else>{{ item.value.requiresNPI ? "✔️" : "" }}</template>
            </template>
            <template #cell(actions)="{ item, index }">
                <div class="d-flex" style="gap: 6px;">
                    <template v-if="editingRowIndex !== index">
                        <b-button variant="link" @click.prevent="startTableEdit(item, index)" title="Edit">
                            <b-icon icon="pencil" variant="primary" />
                        </b-button>
                    </template>
                    <template v-else>
                        <b-button variant="link" title="Save" @click.prevent="saveTableEdit(index)">
                            <b-icon icon="save" variant="primary" />
                        </b-button>
                        <b-button variant="link" title="Cancel Edit" @click.prevent="cancelTableEdit">
                            <b-icon icon="x-lg" variant="" />
                        </b-button>
                    </template>
                </div>
            </template>
        </b-table>

        <b-button size="sm" variant="primary" @click="addTableItem">
            Add Speciality
        </b-button>
    </div>

</template>

<script lang="ts">
import { Speciality } from "@/models/Speciality";
import { Vue, Prop, Component } from "vue-property-decorator";

@Component({
    name: "SpecialitiesField",
})
export default class SpecialitiesField extends Vue {
    @Prop({ default: () => [] }) value!: Speciality[];
    @Prop({ type: String, default: 'Add' }) addLabel!: string;

    // Local component data with proper types
    editingRowData: Speciality | undefined = undefined;
    editingRowIndex: number = -1;

    startTableEdit(item: { value: Speciality }, index: number): void {
        this.editingRowIndex = index;
        this.editingRowData = item.value;
    }

    async addTableItem() {
        //If currently editing, commit the edit first
        if (this.editingRowIndex >= 0) {
            this.saveTableEdit(this.editingRowIndex);
            await this.$nextTick();
        }

        const newItem = { id: 0, name: "", requiresNPI: true };

        const cleanedArray = this.value.filter((a) => a.name); // Trimmed array to remove empty values
        cleanedArray.push(newItem); // Push a new empty string to the array

        // Emit the updated array and enter edit mode for the newly added item
        this.$emit("input", cleanedArray);
        this.startTableEdit({ value: newItem }, cleanedArray.length - 1);
    }

    removeTableItem(index: number): void {
        const array = [...this.value];
        array.splice(index, 1); // Remove the element at the given index
        this.$emit("input", array); // Emit the updated array
    }

    cancelTableEdit(): void {
        const editingIndex = this.editingRowIndex;
        const editingValue = this.value[editingIndex];

        // If the value being edited is empty, remove it
        if (!editingValue) {
            this.removeTableItem(editingIndex);
        }

        // Reset editing state
        this.editingRowIndex = -1;
        this.editingRowData = undefined;
    }

    saveTableEdit(index: number): void {
        const array = [...this.value];
        const newValue = this.editingRowData;

        // Update the value or remove the item based on the trimmed value
        if (!newValue) {
            array.splice(index, 1);
        } else {
            array.splice(index, 1, newValue);
        }

        // Emit the updated array and reset editing state
        this.$emit("input", array);
        this.editingRowIndex = -1;
        this.editingRowData = undefined;
    }
}
</script>

<style scoped lang="scss">
table ::v-deep(.actions) {
    width: 50px;
}
</style>
